import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import CourierLayout from "../components/CourierLayout";
import { Bell, Check, Trash2, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

export default function CourierNotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers: getHeaders() });
      setNotifications(res.data.notifications || res.data || []);
    } catch (e) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers: getHeaders() });
      load();
    } catch (e) {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: getHeaders() });
      toast.success("Toutes les notifications marquees comme lues");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const unreadCount = notifications.filter(n => n.status === "unread").length;

  return (
    <CourierLayout title="Notifications" subtitle={`${unreadCount} non lue(s)`}>
      <div data-testid="courier-notifications-page" className="space-y-4">
        {unreadCount > 0 && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={markAllRead} className="text-xs border-white/10 text-slate-400 hover:text-white gap-1.5" data-testid="mark-all-read">
              <Check className="w-3.5 h-3.5" /> Tout marquer comme lu
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16 bg-slate-900/60 rounded-xl border border-white/5">
            <Bell className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucune notification</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map(notif => (
              <div
                key={notif.id}
                data-testid={`notif-${notif.id}`}
                className={`bg-slate-900/60 border rounded-xl p-4 transition-colors ${
                  notif.status === "unread" ? "border-emerald-500/20 bg-emerald-500/5" : "border-white/5"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    notif.type === "course" ? "bg-emerald-500/15" : "bg-blue-500/100/15"
                  }`}>
                    {notif.type === "course" ? <Package className="w-4 h-4 text-emerald-400" /> : <Bell className="w-4 h-4 text-blue-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{notif.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{notif.message}</p>
                    <p className="text-[10px] text-slate-600 mt-1">{new Date(notif.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  {notif.status === "unread" && (
                    <button
                      onClick={() => markRead(notif.id)}
                      className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-emerald-400 flex-shrink-0"
                      data-testid={`mark-read-${notif.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CourierLayout>
  );
}
